import { useMemo } from 'react';
import { usePipPlayerStore } from '@/stores/pip-player.store';
import { VideoPlayerProvider } from '@/components/VideoPlayer/VideoPlayerProvider';
import { StreamingMode } from '@/types/streaming';
import type { FullSource } from '@/types/source';
import type { Event } from '@/types/event';
import type { EventPlaybackRange } from '@/types/video-player';

import { PipPlayerLayout } from './components/PipPlayerLayout';
import { getEventPlaybackStartTime } from '@/utils/event-duration';
import { buildBoundingBoxProps } from '@/utils/bounding-box';

interface PipPlayerProps {
    event: Event;
    source: FullSource;
    confidence?: number;
    focuses?: number[][];
    startTime?: number;
    eventRange?: EventPlaybackRange;
}

export function PipPlayer({
    event,
    source,
    confidence,
    focuses,
    startTime: startTimeOverride,
    eventRange,
}: PipPlayerProps) {
    const setIsPlaying = usePipPlayerStore((s) => s.setIsPlaying);
    const pauseToken = usePipPlayerStore((s) => s.pauseToken);
    const startTime =
        startTimeOverride ?? getEventPlaybackStartTime(event, focuses);

    const boundingBoxProps = useMemo(
        () =>
            buildBoundingBoxProps({
                event,
                focuses,
            }),
        [event, focuses],
    );

    return (
        <VideoPlayerProvider
            src={source.src || ''}
            source={source}
            initialMode={StreamingMode.VOD}
            startTime={startTime}
            eventRange={eventRange}
            boundingBoxProps={boundingBoxProps}
            defaultLoop
            autoPlay
            onPlayingChange={setIsPlaying}
            pauseSignal={pauseToken}
        >
            <PipPlayerLayout
                boundingBoxProps={boundingBoxProps}
                source={source}
                startTime={startTime}
            />
        </VideoPlayerProvider>
    );
}
